import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Check, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error("Error fetching packages:", error);
      // Fallback data
      setPackages([
        {
          id: "small",
          name: "Small Package",
          price: 1000,
          guests: "0-50",
          description: "Perfect for intimate gatherings and small parties.",
          features: [
            "Includes £450 dry hire fee",
            "One 30L keg from the menu",
            "12 Bottles of wine from the menu",
            "Setup, DJ, PA System and Staff"
          ]
        },
        {
          id: "medium",
          name: "Medium Package",
          price: 1550,
          guests: "50-100",
          description: "Ideal for medium-sized events, weddings, and celebrations.",
          features: [
            "Includes £450 dry hire fee",
            "One 50L keg from the menu",
            "24 Bottles of wine from the menu",
            "Setup, DJ, PA System and Staff"
          ]
        },
        {
          id: "large",
          name: "Large Package",
          price: 2100,
          guests: "100-150",
          description: "The ultimate experience for large festivals and grand weddings.",
          features: [
            "Includes £450 dry hire fee",
            "One 50L keg and one 30L keg",
            "36 Bottles of wine from the menu",
            "Setup, DJ, PA System and Staff"
          ]
        },
        {
          id: "dj_only",
          name: "DJ & Soundsystem Only",
          price: 500,
          guests: "Any",
          description: "Just the music - professional DJ and PA system for up to 5 hours.",
          features: [
            "Professional DJ",
            "Full PA System",
            "Up to 5 hours",
            "Setup included"
          ],
          isDJOnly: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="pricing-page">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#050505]">
        <div className="container-custom text-center">
          <p className="label-text text-yellow-400 mb-4">Packages</p>
          <h1 className="heading-xl text-white mb-6">Design Your Event</h1>
          <p className="text-body max-w-2xl mx-auto">
            Choose your package based on guest count. All packages include the £450 dry hire fee, 
            setup, DJ, PA system, and professional staff.
          </p>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="pb-24 bg-[#050505]" data-testid="packages-section">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {packages.map((pkg, index) => (
                <div 
                  key={pkg.id} 
                  className={`package-card relative ${index === 1 ? 'border-yellow-400/50' : ''}`}
                  data-testid={`package-card-${pkg.id}`}
                >
                  {index === 1 && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-black px-4 py-1 text-xs font-bold uppercase tracking-widest">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <p className="label-text text-yellow-400 mb-2">{pkg.guests} Guests</p>
                    <h3 className="heading-md text-white mb-2">{pkg.name}</h3>
                    <p className="text-body text-sm">{pkg.description}</p>
                  </div>

                  <div className="mb-8">
                    <span className="text-4xl font-black text-white">£{pkg.price.toLocaleString()}</span>
                    <span className="text-neutral-500 ml-2">total</span>
                  </div>

                  <ul className="space-y-4 mb-8 flex-grow">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-neutral-300">
                        <Check className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link 
                    to={`/book/${pkg.id}`}
                    className={`w-full text-center ${index === 1 ? 'btn-primary' : 'btn-secondary'}`}
                    data-testid={`book-package-${pkg.id}`}
                  >
                    Book This Package
                    <ChevronRight className="inline ml-2 w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Add-ons Preview */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="addons-preview">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="label-text text-yellow-400 mb-4">Customize Your Package</p>
            <h2 className="heading-lg text-white mb-4">Available Add-ons</h2>
            <p className="text-body max-w-2xl mx-auto">
              Enhance your event with additional drinks and services. 
              You can customize these when booking your package.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="surface-card p-6" data-testid="addon-preview-cocktails">
              <h4 className="text-white font-semibold mb-2">Cocktails</h4>
              <p className="text-neutral-400 text-sm mb-3">Aperol Spritz, Pimms, Moscow Mule, Espresso Martini, G&T</p>
              <p className="text-yellow-400 font-bold">From £500</p>
            </div>
            <div className="surface-card p-6" data-testid="addon-preview-spirits">
              <h4 className="text-white font-semibold mb-2">Spirits & Mixers</h4>
              <p className="text-neutral-400 text-sm mb-3">Vodka, Gin, Rum, Whiskey, Tequila with mixers</p>
              <p className="text-yellow-400 font-bold">From £200</p>
            </div>
            <div className="surface-card p-6" data-testid="addon-preview-wine">
              <h4 className="text-white font-semibold mb-2">Extra Wine</h4>
              <p className="text-neutral-400 text-sm mb-3">Premium selection of white, red, rosé & bubbles</p>
              <p className="text-yellow-400 font-bold">6 bottles £140 / 12 bottles £250</p>
            </div>
            <div className="surface-card p-6" data-testid="addon-preview-kegs">
              <h4 className="text-white font-semibold mb-2">Extra Kegs</h4>
              <p className="text-neutral-400 text-sm mb-3">Estrella, Becks, Birra Moretti, Firebird beers</p>
              <p className="text-yellow-400 font-bold">30L £180 / 50L £230</p>
            </div>
          </div>
        </div>
      </section>

      {/* Deposit Info */}
      <section className="py-16 bg-[#050505]" data-testid="deposit-info">
        <div className="container-custom">
          <div className="surface-card p-8 md:p-12 text-center max-w-3xl mx-auto">
            <h3 className="heading-md text-white mb-4">Secure Your Date</h3>
            <p className="text-body mb-6">
              Book your event with a <span className="text-yellow-400 font-bold">£450 deposit</span>. 
              The remaining balance is due 2 weeks before your event.
            </p>
            <Link to="/contact" className="btn-secondary" data-testid="contact-questions">
              Have Questions? Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
